// dodatkowe filtry pod listą kategorii

import React, { FC, useEffect, useState } from 'react';
import { ExclamationCircle, SortDown, ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import classnames from 'classnames';

import { IProductsFilter, IProductsFilterValue } from 'api/types';
import { Button, Input, Range } from 'components/controls';
import WiseSelect from 'components/wise/WiseSelect';

import styles from 'theme/pages/Products/components/FiltersSidebar/FiltersSidebar.module.scss';

interface IFilter {
  filter_id: string;
  filter_value: string;
}

// typ danych wejściowych
interface IProps {
  categoryId?: number;
  queryFilters: IFilter[];
  filtersData?: IProductsFilter[];
  onChange: (filters: IFilter[]) => void;
}

const FiltersSidebar: FC<IProps> = ({ categoryId, onChange, queryFilters, filtersData }) => {
  const { t } = useTranslation();

  // lista zqaznaczonych filtrów
  const [currentProductsFilters, setCurrentProductsFilters] = useState<
    { filter_id: string; filter_value: string }[]
  >([]);

  // stan zwijania/rozwijania filtrów
  const [isExpanded, setIsExpanded] = useState(false);

  // nadpisanie lokalnych filtrów przy zmienie filtrów z props'ów
  useEffect(() => {
    setCurrentProductsFilters(queryFilters);
  }, [queryFilters]);

  // liczba filtrów do wyświetlenia
  const MAX_VISIBLE_FILTERS = 10;
  const visibleFilters = filtersData?.slice(0, MAX_VISIBLE_FILTERS) || [];
  const hiddenFilters = filtersData?.slice(MAX_VISIBLE_FILTERS) || [];
  const hasMoreFilters = (filtersData?.length || 0) > MAX_VISIBLE_FILTERS;

  // Funkcja pomocnicza do aktualizacji filtrów
  const updateFilter = (filterId: string, filterValue: string | null) => {
    setCurrentProductsFilters((prevState) => {
      const newState = prevState.filter(
        (assignedFilter) => assignedFilter.filter_id !== filterId
      );
      
      if (filterValue) {
        newState.push({ filter_id: filterId, filter_value: filterValue });
      }
      
      return newState;
    });
  };

  const getFilter = (filter: IProductsFilter) => {
    const currentFilter = currentProductsFilters.find(
      (item) => item.filter_id === filter.id
    );
    const currentValue = currentFilter?.filter_value;

    switch (filter.type) {
      case 'singlechoice':
        return (
          <WiseSelect<IProductsFilterValue>
            value={currentValue}
            styleGroup={'filtry-boczne'}
            onChange={(item) => {
              updateFilter(filter.id, item ? item.value : null);
              if (!item) {
                onChange(
                  currentProductsFilters.filter((f) => f.filter_id !== filter.id)
                );
              }
            }}
            options={filter.values.map((item) => ({
              value: item.value,
              label: item.name,
              item
            }))}
            placeholder={filter.label}
            clearable
          />
        );

      case 'multichoice':
        return (
          <WiseSelect<IProductsFilterValue>
            mode="multichoice"
            value={currentValue}
            variant={'default'}
            styleGroup={'filtry-boczne'}
            separatorMultiChoice=","
            onChange={(items) => {
              if (Array.isArray(items) && items.length > 0) {
                const values = items
                  .map((item) => item.value)
                  .filter(Boolean)
                  .join(',');
                updateFilter(filter.id, values);
              } else {
                updateFilter(filter.id, null);
                onChange(
                  currentProductsFilters.filter((f) => f.filter_id !== filter.id)
                );
              }
            }}
            options={filter.values.map((item) => ({
              value: item.value,
              label: item.name,
              item
            }))}
            placeholder={filter.label}
            clearable
          />
        );

      case 'between': {
        // Przygotuj dane dla Range: oblicz min i max z całej tablicy wartości
        const numericValues = filter.values
          .map((v) => parseFloat(v.value))
          .filter((v) => !isNaN(v));
        const minValue = Math.min(...numericValues);
        const maxValue = Math.max(...numericValues);

        // Utwórz obiekt filtra z tylko dwiema wartościami (min i max) dla Range
        const rangeFilter = {
          ...filter,
          values: [
            { name: minValue.toString(), value: minValue.toString() },
            { name: maxValue.toString(), value: maxValue.toString() }
          ]
        };

        return (
          <Range
            filter={rangeFilter}
            className={styles.rangeButton}
            separator={':'}
            queryFilters={currentProductsFilters.map((f) => ({
              filter_id: f.filter_id,
              filter_value:
                f.filter_id === filter.id
                  ? f.filter_value.split(':').join(';')
                  : f.filter_value,
              filter_type: filter.type
            }))}
            onChange={(filters, currentFilterId) => {
              const updatedFilters = filters.map((f) => ({
                filter_id: f.filter_id,
                filter_value:
                  f.filter_id === currentFilterId
                    ? f.filter_value.split(';').join(':')
                    : f.filter_value
              }));
              setCurrentProductsFilters(updatedFilters);
              // Wywołaj onChange zawsze, niezależnie od tego czy filtr został dodany czy usunięty
              onChange(updatedFilters);
            }}
          />
        );
      }

      case 'like':
        return (
          <Input
            type="text"
            value={currentValue || ''}
            onChange={(value) => {
              updateFilter(filter.id, value || null);
            }}
            placeholder={filter.label}
          />
        );

      default:
        return (
          <div className={styles.unknownType}>
            {t('Nieznany typ filtra')} (<strong>{filter.type}</strong>)
          </div>
        );
    }
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-Products-components-FiltersSidebar'
      )}>
      {(!categoryId || (filtersData?.length || 0) > 0) && (
        <div className={styles.title}>{t('Filtry')}</div>
      )}

      {!categoryId && (
        <div className={styles.info}>
          <ExclamationCircle />
          <span>
            <ReactMarkdown disallowedElements={['p']} unwrapDisallowed>
              {t('PRODUCTS_SIDEBAR_NO_FILTERS_INFO')}
            </ReactMarkdown>
          </span>
        </div>
      )}

      {!!categoryId && (filtersData?.length || 0) > 0 && (
        <div className={styles.filtersWrapper}>
          {visibleFilters.map((filter) => (
            <div className={styles.filter} key={filter.id}>
              {getFilter(filter)}
            </div>
          ))}

          {hasMoreFilters && (
            <div
              className={classnames(styles.hiddenFilters, {
                [styles.expanded]: isExpanded
              })}>
              {hiddenFilters.map((filter) => (
                <div className={styles.filter} key={filter.id}>
                  {getFilter(filter)}
                </div>
              ))}
            </div>
          )}

          {hasMoreFilters && (
            <div className={styles.expandButton}>
              <Button
                color="secondary"
                onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? (
                  <>
                    {t('Pokaż mniej')} <ChevronUp />
                  </>
                ) : (
                  <>
                    {t('Pokaż więcej')} ({(filtersData?.length ?? 0) - MAX_VISIBLE_FILTERS}){' '}
                    <ChevronDown />
                  </>
                )}
              </Button>
            </div>
          )}

          <div className={styles.actions}>
            <Button color="secondary" onClick={() => onChange(currentProductsFilters)}>
              {t('Filtruj')} <SortDown />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiltersSidebar;
